import {
  TableV2 as Table,
  ColumnType,
  Row,
  Button,
} from "@asayinc/component-library";
import { Box } from "@mui/material";
import { Add } from "@mui/icons-material";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { ErrorState } from "src/components/atoms/ErrorState";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { LoadingCard } from "src/components/molecules/Loading";
import {
  ADDITIONAL_MAILING_TYPE_LABEL,
  ADDITIONAL_MAILING_AUDIENCE_LABEL,
} from "src/constants/labels/additionalMailingLabel";
import { ADDITIONAL_MAILING_REQUEST_STATUS_LABEL } from "src/constants/labels/additionalMailingRequestLabel";
import { useAdditionalMailingRequestsQuery } from "src/store/additionalMailingRequest";
import {
  AdditionalMailingType,
  AdditionalMailingAudience,
} from "src/store/additionalMailing/types";
import {
  IAdditionalMailingRequest,
  AdditionalMailingRequestOperationsStatus,
} from "src/store/additionalMailingRequest/types";
import { CreateUpdateAdditionalMailingRequestDialog } from "./components/CreateUpdateAdditionalMailingRequestDialog";
import { DeleteAdditionalMailingRequestDialog } from "./components/DeleteAdditionalMailingRequestDialog";
import { AdditionalMailingRequestDetailsDialog } from "./components/AdditionalMailingRequestDetailsDialog";

const COLUMNS = ["type", "audience", "sendDate", "minimumShares", "status"];

type AdditionalMailingRequestColumnData = {
  type: ColumnType;
  audience: ColumnType;
  sendDate: ColumnType;
  minimumShares: ColumnType;
  status: ColumnType;
};

function getAdditionalMailingRequestColumnData(): AdditionalMailingRequestColumnData {
  return {
    type: {
      style: "text",
      title: "Type",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
    },
    audience: {
      style: "text",
      title: "Audience",
    },
    sendDate: {
      style: "text",
      title: "Send Date",
    },
    minimumShares: {
      style: "text",
      title: "Min Shares",
    },
    status: {
      style: "text",
      title: "Status",
    },
  };
}

function getRowData(
  requests: IAdditionalMailingRequest[],
  onRowClick: (request: IAdditionalMailingRequest) => void
): Row[] {
  const handleRowClick = (data: unknown) => {
    const { request } = data as {
      request: IAdditionalMailingRequest;
      idx: number;
    };
    onRowClick(request);
  };

  return requests.map((request, idx) => {
    const { id, type, audience, sendDate, minimumShares, operationsStatus } =
      request;

    return {
      id,
      data: { request, idx },
      rowData: {
        type: {
          content:
            ADDITIONAL_MAILING_TYPE_LABEL[type as AdditionalMailingType] ||
            type,
          clickHandler: handleRowClick,
        },
        audience: {
          content:
            ADDITIONAL_MAILING_AUDIENCE_LABEL[
              audience as AdditionalMailingAudience
            ] ||
            audience ||
            "-",
          clickHandler: handleRowClick,
        },
        sendDate: {
          content: sendDate ? dayjs(sendDate).format("MM/DD/YYYY") : "-",
          clickHandler: handleRowClick,
        },
        minimumShares: {
          content: minimumShares?.toLocaleString() || "0",
          clickHandler: handleRowClick,
        },
        status: {
          content:
            ADDITIONAL_MAILING_REQUEST_STATUS_LABEL[
              operationsStatus as AdditionalMailingRequestOperationsStatus
            ] || operationsStatus,
          clickHandler: handleRowClick,
        },
      },
    };
  });
}

export function ProxyEventAdditionalMailingRequestsTab() {
  const { id } = useParams() as { id: string };
  const {
    data: requestsData,
    isLoading: isLoadingAdditionalMailingRequests,
    error: additionalMailingRequestsError,
  } = useAdditionalMailingRequestsQuery({ proxyEventId: id });
  const requests = useMemo(
    () => requestsData?.results || [],
    [requestsData?.results]
  );

  const [selectedRequest, setSelectedRequest] =
    useState<IAdditionalMailingRequest | null>(null);
  const [isCreateEditDialogOpen, setIsCreateEditDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] =
    useState<IAdditionalMailingRequest | null>(null);
  const [deletingRequestId, setDeletingRequestId] = useState<string | null>(
    null
  );

  const handleRowClick = (request: IAdditionalMailingRequest) => {
    setSelectedRequest(request);
  };

  const handleCloseDetailsDialog = () => {
    setSelectedRequest(null);
  };

  const handleEdit = (request: IAdditionalMailingRequest) => {
    setSelectedRequest(null);
    setEditingRequest(request);
    setIsCreateEditDialogOpen(true);
  };

  const handleDelete = (request: IAdditionalMailingRequest) => {
    setSelectedRequest(null);
    setDeletingRequestId(request.id);
  };

  const handleOpenCreateDialog = () => {
    setEditingRequest(null);
    setIsCreateEditDialogOpen(true);
  };

  const handleCloseCreateEditDialog = () => {
    setIsCreateEditDialogOpen(false);
  };

  const handleCloseDeleteDialog = () => {
    setDeletingRequestId(null);
  };

  const columns = useMemo(() => COLUMNS, []);
  const columnData = useMemo(() => getAdditionalMailingRequestColumnData(), []);
  const rowData = useMemo(
    () => getRowData(requests, handleRowClick),
    [requests]
  );

  if (additionalMailingRequestsError) {
    return <ErrorState />;
  }

  if (isLoadingAdditionalMailingRequests) {
    return (
      <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
        <LoadingCard />
      </ScrollContainer>
    );
  }

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Box sx={{ mb: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="primary"
          size="small"
          startIcon={<Add />}
          onClick={handleOpenCreateDialog}
        >
          Create Request
        </Button>
      </Box>
      <Box>
        <Table
          count={requests.length}
          columnData={columnData}
          columns={columns}
          isFetching={false}
          isLoading={isLoadingAdditionalMailingRequests}
          noResultsData={{
            title: "No Additional Mailing Requests",
            subtitle:
              "There are no additional mailing requests for this proxy event. Click 'Create Request' to add one.",
          }}
          rows={rowData}
          tableMinWidth={700}
          title="Additional Mailing Requests"
          titleVariant="subtitle2"
        />
      </Box>

      <AdditionalMailingRequestDetailsDialog
        open={!!selectedRequest}
        onClose={handleCloseDetailsDialog}
        request={selectedRequest}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <CreateUpdateAdditionalMailingRequestDialog
        open={isCreateEditDialogOpen}
        onClose={handleCloseCreateEditDialog}
        proxyEventId={id}
        request={editingRequest}
      />

      <DeleteAdditionalMailingRequestDialog
        open={!!deletingRequestId}
        onClose={handleCloseDeleteDialog}
        proxyEventId={id}
        requestId={deletingRequestId}
      />
    </ScrollContainer>
  );
}
